import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Loader2, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/partner/whi/registrations`;
const KEY = 'wekeza-whi-partner-key';
const STATUS_CLS = { pending_whi_registration: 'bg-sky-50 text-sky-700', needs_info: 'bg-amber-50 text-amber-700',
  completed: 'bg-violet-50 text-violet-700', payment_reported: 'bg-orange-50 text-orange-700', payment_confirmed: 'bg-emerald-50 text-emerald-700' };
const errMsg = (e) => { const d = e.response?.data?.detail; return (Array.isArray(d) ? d[0]?.msg : typeof d === 'string' ? d : null) || 'Failed'; };
const inp = 'flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm';

const PaymentRow = ({ r, p, act }) => {
  const [units, setUnits] = useState('');
  return (
    <li className="flex flex-col gap-2 py-2 text-xs sm:flex-row sm:items-center" data-testid={`whi-payment-${p.id}`}>
      <span className="flex-1">TZS {Number(p.amount_tzs).toLocaleString()} · ref <b className="font-mono">{p.reference}</b> · {new Date(p.reported_at).toLocaleString()}</span>
      {p.status === 'confirmed' ? <span className="font-semibold text-emerald-700">{p.units} units confirmed</span> : (
        <>
          <input type="number" step="0.0001" value={units} onChange={(e) => setUnits(e.target.value)} placeholder="Units issued" className="rounded-lg border border-slate-300 px-2 py-1" data-testid={`whi-units-${p.id}`} />
          <button disabled={!(Number(units) > 0)} onClick={() => act(`payments/${p.id}/confirm`, { units: Number(units) }, 'Units confirmed — client notified')}
            className="rounded-lg bg-emerald-700 px-3 py-1 font-semibold text-white disabled:opacity-50" data-testid={`whi-confirm-${p.id}`}>Confirm units</button>
        </>
      )}
    </li>
  );
};

const RegCard = ({ r, headers, onDone }) => {
  const [acct, setAcct] = useState(r.account_number || '');
  const [ctrl, setCtrl] = useState(r.control_number || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await axios.post(`${API}/${r.lead_id}/${path}`, body, { headers }); toast.success(ok); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`whi-reg-${r.lead_id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold">{r.lead_id}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[r.status] || ''}`} data-testid={`whi-reg-status-${r.lead_id}`}>{r.status.replace(/_/g, ' ')}</span>
        <span className="text-xs text-slate-500">{r.source} · {r.created_at && new Date(r.created_at).toLocaleString()}</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        {[['Name', r.full_name], ['Phone', r.phone], ['Email', r.email], ['NIDA', r.nida], ['First deposit', r.desired_amount_tzs && `TZS ${r.desired_amount_tzs.toLocaleString()}`]].map(([l, v]) => (
          <div key={l}><dt className="text-slate-500">{l}</dt><dd className="break-all font-medium text-slate-900">{v || '—'}</dd></div>
        ))}
      </dl>
      {r.status === 'needs_info' && <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">Returned to client: {r.needs_info_reason}</p>}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input value={acct} onChange={(e) => setAcct(e.target.value)} placeholder="Faida account number" className={inp} data-testid={`whi-acct-${r.lead_id}`} />
        <input value={ctrl} onChange={(e) => setCtrl(e.target.value)} placeholder="GePG control number" className={inp} data-testid={`whi-ctrl-${r.lead_id}`} />
        <button disabled={busy || acct.trim().length < 4 || ctrl.trim().length < 6} onClick={() => act('issue', { account_number: acct, control_number: ctrl }, 'Numbers sent to client')}
          className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid={`whi-issue-${r.lead_id}`}>{r.control_number ? 'Update numbers' : 'Issue numbers'}</button>
      </div>
      {r.status === 'pending_whi_registration' && (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What is missing?" className={inp} data-testid={`whi-reason-${r.lead_id}`} />
          <button disabled={busy || reason.trim().length < 3} onClick={() => act('needs-info', { reason }, 'Returned to client')}
            className="rounded-lg border border-amber-400 px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-50" data-testid={`whi-needs-info-${r.lead_id}`}>Needs info</button>
        </div>
      )}
      {!!r.payments?.length && <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">{r.payments.map((p) => <PaymentRow key={p.id} r={r} p={p} act={act} />)}</ul>}
    </div>
  );
};

const KeyGate = ({ onKey }) => {
  const [input, setInput] = useState('');
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <form onSubmit={(e) => { e.preventDefault(); onKey(input.trim()); }} className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-sm" data-testid="whi-login">
        <Home className="mb-3 h-8 w-8 text-emerald-700" />
        <h1 className="text-lg font-bold">WHI · Faida Fund Partner Desk</h1>
        <p className="mb-4 text-xs text-slate-500">Registrations and payments submitted through Wekeza-Smart.</p>
        <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Partner access key" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="whi-key-input" />
        <button className="w-full rounded-lg bg-emerald-700 py-2 text-sm font-semibold text-white" data-testid="whi-login-btn">Open desk</button>
      </form>
    </div>
  );
};

export default function WhiDesk() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY) || '');
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState(null);
  const headers = { 'X-Partner-Key': key };

  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}${status ? `?status=${status}` : ''}`, { headers: { 'X-Partner-Key': key } })).data.rows); }
    catch { toast.error('Invalid partner key'); sessionStorage.removeItem(KEY); setKey(''); }
  }, [key, status]);
  useEffect(() => { if (key) load(); }, [key, load]);

  if (!key) return <KeyGate onKey={(k) => { sessionStorage.setItem(KEY, k); setKey(k); }} />;
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold">WHI Partner Desk — Faida Fund</h1>
          <button onClick={() => { sessionStorage.removeItem(KEY); setKey(''); }} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500" data-testid="whi-logout"><LogOut className="h-3.5 w-3.5" />Lock</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {[['open', 'New'], ['completed', 'Awaiting payment'], ['payment_reported', 'Payment reported'], ['payment_confirmed', 'Confirmed'], ['', 'All']].map(([v, l]) => (
            <button key={l} onClick={() => setStatus(v)} data-testid={`whi-filter-${l.toLowerCase().replace(/ /g, '-')}`}
              className={`rounded-full px-3 py-1 text-xs font-medium ${status === v ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600'}`}>{l}</button>
          ))}
        </div>
        {rows === null ? <Loader2 className="h-5 w-5 animate-spin" /> : rows.length === 0
          ? <p className="text-sm text-slate-500" data-testid="whi-empty">No registrations.</p>
          : rows.map((r) => <RegCard key={r.lead_id} r={r} headers={headers} onDone={load} />)}
      </div>
    </div>
  );
}
